import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { catchError, map, Observable, throwError } from 'rxjs';

import { environment } from '@environments/environment';
import { ApiResponse, ErrorLoggingOption } from '@app/types/api-service.types';
import { LoggerService } from '@app/common/services/logger.service';
import { LoggerType } from '@app/types/logger.types';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private httpClient = inject(HttpClient);
  private loggerService = inject(LoggerService);

  private urlBase = environment.apiHost;

  mpGet<T>(apiCallUrl: string, getParams?: Record<string, string | number | boolean | [string | number | boolean]>): Observable<T> {
    return this.httpClient.get<ApiResponse<T>>(`${this.urlBase}${apiCallUrl}`, { params: getParams, responseType: 'json' }).pipe(
      catchError((e) => this.handleError(e, true)),
      map((response: ApiResponse<T>) => response.data)
    );
  }

  mpPost<T>(apiCallUrl: string, data?: object) {
    return this.httpClient.post<T>(`${this.urlBase}${apiCallUrl}`, data, { withCredentials: true }).pipe(catchError((e) => this.handleError(e, false)));
  }

  mpPatch<T>(apiCallUrl: string, data?: object) {
    return this.httpClient.patch<T>(`${this.urlBase}${apiCallUrl}`, data, { withCredentials: true }).pipe(catchError((e) => this.handleError(e, false)));
  }

  mpPut<T>(apiCallUrl: string, data?: object) {
    return this.httpClient.put<T>(`${this.urlBase}${apiCallUrl}`, data, { withCredentials: true }).pipe(catchError((e) => this.handleError(e, false)));
  }

  mpDelete<T>(apiCallUrl: string, data?: object) {
    return this.httpClient.delete<T>(`${this.urlBase}${apiCallUrl}`, data).pipe(catchError((e) => this.handleError(e, false)));
  }

  private handleError(error: HttpErrorResponse, isReadOperation: boolean, logErrorOption = ErrorLoggingOption.ShowActualError) {
    // The backend returned an unsuccessful response code.
    // The response body may contain clues as to what went wrong,
    console.error(`Backend returned code ${error.status}, ` + `body was:`, error?.error);

    if (logErrorOption === ErrorLoggingOption.DoNotShowError) {
      return throwError(() => error);
    }

    // Display a generic toast error to the user
    const genericErrorMessage = isReadOperation ? 'Failed to load data from the server. If this error persists, please notify support.' : 'Failed to save data to the server. If this error persists, please notify support.';

    if (logErrorOption === ErrorLoggingOption.ShowGenericError) {
      this.loggerService.log(LoggerType.Error, genericErrorMessage);
      return throwError(() => error);
    }

    // Try to show the actual error
    const errorMessage = ApiService.getMessageFromError(error);

    this.loggerService.log(LoggerType.Error, errorMessage ?? genericErrorMessage);
    return throwError(() => error);
  }

  private static isInvalidErrorMessage(errorMessage: string): boolean {
    const error = errorMessage?.toLowerCase() || '';
    return error.includes('stacktrace') || error.includes('stack trace');
  }

  public static getMessageFromError(error: HttpErrorResponse): string {
    let errorMessage = '';

    if (error.error && error.error.errors && typeof error.error.errors === 'object') {
      errorMessage =
        '<span>' +
        Object.values(error.error.errors)
          .map((e) => e['message'])
          .join('</span><span>') +
        '</span>';
    }

    // Non-500 errors are usually sent deliberately by the backend, so we want to show the actual error message
    const showActualError = error.status !== 500 && !this.isInvalidErrorMessage(errorMessage); // don't log unhandled stack traces - may need to refine this check further

    return showActualError ? errorMessage : '';
  }
}
